import { createQueryKeys } from '@lukemorales/query-key-factory';

import { getData } from '../utils/functional/fp';

import {
  fetchSubmissionsMetrics,
  fetchSubmissionsStatuses,
  getSubmissionById,
  getSubmissionByMsgId,
  getSubmissionEligibleProducts,
  getSubmissionMergedQuestions,
  getSubmissionsUsers,
  getSubmissionClaimsStatus,
  getSubmissionWithConfidenceById,
  searchSubmissions,
} from '../api/submissions/submissions.api';
import type { Nullable } from '../types/common/utility';

export const submissions = createQueryKeys('submissions', {
  // No `enabled` on `details`/`detailsWithConfidence`/`claimsStatus` below:
  // createQueryKeys only forwards `queryKey`/`queryFn`/`contextQueries` out
  // of a factory entry's return value, so any other property — these three
  // used to carry `enabled: !!submissionId` — is silently dropped before a
  // caller's useQuery(submissions.details(id)) ever sees it. Every real
  // caller already sets its own `enabled` at the useQuery call site; keep
  // doing that rather than relying on this factory to gate the query
  // (OMNI-773 hit the same stripping with `retry` in queries/ask-moo.ts).
  details: (submissionId: Nullable<string>) => ({
    queryKey: [{ submissionId }],
    queryFn: () => getSubmissionById(submissionId!).then(getData),
  }),

  detailsWithConfidence: (submissionId: Nullable<string>) => ({
    queryKey: [{ submissionId }],
    queryFn: () => getSubmissionWithConfidenceById(submissionId!).then(getData),
  }),

  detailsByMsgId: (msgId: string) => ({
    queryKey: [{ msgId }],
    queryFn: async () => {
      try {
        const response = await getSubmissionByMsgId(msgId);
        return getData(response);
      } catch (err: any) {
        if (err?.response?.status === 404) {
          return null; // treat 404 as "no submission found"
        }
        throw err; // rethrow other errors
      }
    },
  }),
  list: (query: Record<string, unknown>) => ({
    queryKey: [{ query }],
    queryFn: () => searchSubmissions({ params: query }).then(getData),
  }),

  metrics: (query: Record<string, unknown>) => ({
    queryKey: [{ query }],
    queryFn: () => fetchSubmissionsMetrics({ params: query }).then(getData),
  }),

  // TODO: Query needs to be adjusted for workspace only submissions
  workspace: (query: Record<string, unknown>) => ({
    queryKey: [{ query }],
    queryFn: () => searchSubmissions({ params: query }).then(getData),
  }),

  statuses: () => ({
    queryKey: ['statuses'],
    queryFn: () => fetchSubmissionsStatuses().then(getData),
  }),

  submissionsUsers: () => ({
    queryKey: ['submissions-users'],
    queryFn: () => getSubmissionsUsers().then(getData),
  }),

  eligibleProducts: (submissionId: string) => ({
    queryKey: [{ submissionId }],
    queryFn: () => getSubmissionEligibleProducts(submissionId).then(getData),
  }),

  // Products on every line the tenant sells, not only the submission's own (Add Coverage Option).
  eligibleProductsAcrossLines: (submissionId: string) => ({
    queryKey: [{ submissionId }, 'across-product-lines'],
    queryFn: () =>
      getSubmissionEligibleProducts(submissionId, {
        params: { acrossProductLines: true },
      }).then(getData),
  }),

  // Keyed by the coverage options' products too, so a change to them by any route refetches the form.
  mergedQuestions: (submissionId: string, productKeys: readonly string[]) => ({
    queryKey: [{ submissionId }, 'merged-questions', productKeys],
    queryFn: async () => {
      try {
        return await getSubmissionMergedQuestions(submissionId).then(getData);
      } catch (err: any) {
        // 409: the service holds no catalogue product among the options, so there is no form to draw. The page
        // renders without it; an error here would hold it behind the router's spinner.
        if (err?.response?.status === 409) {
          return null;
        }
        throw err;
      }
    },
  }),

  // No `enabled` here either — see the comment on `details` above.
  claimsStatus: (submissionId: Nullable<string>) => ({
    queryKey: [{ submissionId }, 'claims-status'],
    queryFn: () => getSubmissionClaimsStatus(submissionId).then(getData),
  }),
});
